import { type UseQueryResult } from "@tanstack/react-query";

import { PlayOnePagerNotice } from "@softmax/product-ux/play";

import type { PublicPlayShell } from "../../lib/types";

import { BoardRows } from "./board-rows";

import { LeaderboardUnavailable } from "./leaderboard-unavailable";

import type { OnePagerData, LeaderboardData } from "../types";

type BoardResource = {
  readonly shell: PublicPlayShell;
  readonly data: LeaderboardData;
};

type BoardRead = Pick<
  UseQueryResult<BoardResource>,
  "data" | "isError" | "isPending" | "isFetching" | "refetch"
>;

export function OnePagerLeaderboard({
  personal,
  publicBoard,
  path,
  refreshPersonal,
  refreshingPersonal,
}: {
  readonly personal: OnePagerData | undefined;
  readonly publicBoard: BoardRead;
  readonly path: string;
  readonly refreshPersonal: () => void;
  readonly refreshingPersonal: boolean;
}) {
  if (personal && personal.boardStatus !== "unavailable")
    return (
      <BoardRows
        data={personal}
        path={path}
        terminalFailure
        pending={refreshingPersonal}
        onRetry={refreshPersonal}
      />
    );

  const data = publicBoard.data?.data;

  if (!data)
    return publicBoard.isPending ? (
      <PlayOnePagerNotice
        state={{ kind: "loading", message: "Loading leaderboard…" }}
      />
    ) : (
      <LeaderboardUnavailable
        path={path}
        terminalFailure
        pending={publicBoard.isFetching}
        onRetry={() => void publicBoard.refetch()}
      />
    );

  return (
    <>
      {personal?.boardStatus === "unavailable" ? (
        <PlayOnePagerNotice
          state={{
            kind: "error",
            message:
              "Your leaderboard could not refresh. Showing the public standings.",
            pending: refreshingPersonal,
            onRetry: refreshPersonal,
          }}
        />
      ) : null}
      {publicBoard.isError ? (
        <PlayOnePagerNotice
          state={{
            kind: "error",
            message: "Leaderboard could not refresh. Showing the last update.",
            pending: publicBoard.isFetching,
            onRetry: () => void publicBoard.refetch(),
          }}
        />
      ) : null}
      <BoardRows
        data={data}
        path={path}
        terminalFailure
        pending={publicBoard.isFetching}
        onRetry={() => void publicBoard.refetch()}
      />
    </>
  );
}
